import React from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '../ui/switch';
import { createSubscription, updateSubscription } from '@/services/api/subscriptions';

const FormSchema = z.object({
  includedAmount: z.number(),
  remainingAmount: z.number(),
  paid: z.boolean()
});

interface Props {
  userId: string;
  initialData?: {
    id: string;
    includedAmount: number;
    remainingAmount: number;
    paid: boolean;
  };
  mode: 'edit' | 'create';
  success: any;
}

export default function SubscriptionForm({ initialData, mode = 'create', success, userId }: Props) {
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      includedAmount: initialData?.includedAmount ?? 10,
      remainingAmount: initialData?.remainingAmount ?? 10,
      paid: initialData?.paid ?? true
    }
  });

  // Update form values when initialData changes
  React.useEffect(() => {
    if (initialData) {
      form.reset({
        includedAmount: initialData.includedAmount,
        remainingAmount: initialData.remainingAmount,
        paid: initialData.paid
      });
    }
  }, [initialData, form]);

  async function onSubmit(data: z.infer<typeof FormSchema>) {
    if (mode === 'create') {
      await createSubscription({
        productId: initialData?.id || '',
        userId: userId,
        includedAmount: data.includedAmount,
        remainingAmount: data.remainingAmount,
        paid: data.paid
      });
    } else {
      await updateSubscription(initialData?.id || '', data);
    }

    success();
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="flex space-x-4">
          <FormField
            control={form.control}
            name="remainingAmount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Remaining amount</FormLabel>
                <FormControl>
                  <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="includedAmount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Total amount</FormLabel>
                <FormControl>
                  <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="paid"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Paid</FormLabel>
              <FormControl>
                <Switch checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit">Submit</Button>
      </form>
    </Form>
  );
}
